/**
 * Versions: what has been live, what changed between two of them, and who changed it.
 *
 * The screen a support case starts on. Every audited customer response carries a `configVersion`,
 * and this is where that label becomes a document, a diff and a name.
 *
 * ## Rollback is the reason the list is not just history
 *
 * Any version that was ever published can be republished, which is the fastest fix for a wording
 * that turned out wrong at four on a Thursday. It mints a *new* label rather than reviving the old
 * one, and the list says so — one label pointing at two moments in time would break the only link
 * an audited response has to what its customer saw.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Card,
  EmptyState,
  Modal,
  Select,
  StatusPill,
  Table,
  fetchBotAudit,
  fetchBotVersions,
  fetchVersionDiff,
  rollbackVersion,
  useToast,
  type AuditEntry,
  type BotVersion,
  type Column,
  type VersionDiff,
} from "@nab/shared";

import { useBot } from "../config";
import { VersionBar } from "./VersionBar";

const STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  published: "success",
  pending_approval: "warning",
  draft: "neutral",
  archived: "neutral",
};

export function VersionsPage(): JSX.Element {
  const { t } = useTranslation("app");
  const { save, problem } = useBot();
  const toast = useToast();
  const [versions, setVersions] = useState<BotVersion[]>([]);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [diff, setDiff] = useState<VersionDiff | null>(null);
  const [comparing, setComparing] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [rolling, setRolling] = useState<BotVersion | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, log] = await Promise.all([fetchBotVersions(), fetchBotAudit()]);
      setVersions(list.versions);
      setEntries(log.entries);
    } catch {
      toast.show({ tone: "error", message: t("versions.loadFailed") });
    }
  }, [toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<BotVersion>[] = [
    {
      key: "label",
      header: t("versions.column.label"),
      render: (row) => <span className="font-mono">{row.label}</span>,
    },
    {
      key: "status",
      header: t("versions.column.status"),
      render: (row) => (
        <StatusPill tone={STATUS_TONE[row.status] ?? "neutral"}>
          {t(`versions.status.${row.status}`)}
        </StatusPill>
      ),
    },
    {
      key: "who",
      header: t("versions.column.who"),
      render: (row) => (
        <span className="text-body-sm text-textMuted">
          {row.publishedBy ?? row.createdBy}
          {row.approvedBy ? ` · ${t("versions.approvedBy", { who: row.approvedBy })}` : ""}
        </span>
      ),
    },
    {
      key: "when",
      header: t("versions.column.when"),
      render: (row) => (
        <span className="font-mono text-label-md text-textMuted">
          {(row.publishedAt ?? row.createdAt).slice(0, 16).replace("T", " ")}
        </span>
      ),
    },
    {
      key: "notes",
      header: t("versions.column.notes"),
      render: (row) => row.notes ?? "—",
    },
    {
      key: "actions",
      header: t("versions.column.actions"),
      render: (row) =>
        row.checksum && row.status !== "published" ? (
          <Button size="sm" variant="secondary" onClick={() => setRolling(row)}>
            {t("versions.rollback")}
          </Button>
        ) : null,
    },
  ];

  if (problem) return <EmptyState title={t("notReady")} description={problem} />;

  const options = versions.map((version) => ({
    value: String(version.id),
    label: `${version.label} (${version.status})`,
  }));

  return (
    <div className="flex flex-col">
      <VersionBar />
      <div className="flex flex-col gap-6 p-4">
        <Table
          caption={t("versions.caption")}
          columns={columns}
          rows={versions}
          rowKey={(row) => String(row.id)}
          empty={<EmptyState title={t("versions.empty")} description="" />}
        />

        {/* -- diff ------------------------------------------------------------------ */}
        <Card className="flex flex-col gap-3 p-4">
          <h3 className="text-heading-sm">{t("versions.compare")}</h3>
          <div className="flex flex-wrap items-end gap-3">
            <Select
              label={t("versions.from")}
              value={comparing.from}
              onChange={(event) =>
                setComparing({ ...comparing, from: event.target.value })
              }
              options={[{ value: "", label: "—" }, ...options]}
            />
            <Select
              label={t("versions.to")}
              value={comparing.to}
              onChange={(event) => setComparing({ ...comparing, to: event.target.value })}
              options={[{ value: "", label: "—" }, ...options]}
            />
            <Button
              variant="secondary"
              disabled={!comparing.from || !comparing.to}
              onClick={async () => {
                setDiff(
                  await fetchVersionDiff(Number(comparing.from), Number(comparing.to)),
                );
              }}
            >
              {t("versions.showDiff")}
            </Button>
          </div>

          {diff ? (
            diff.changes.length === 0 ? (
              <p className="text-body-sm text-textMuted">{t("versions.identical")}</p>
            ) : (
              <ul className="space-y-2">
                {diff.changes.map((change) => (
                  <li key={change.path} className="text-body-sm">
                    <code className="font-mono text-label-md text-primary">
                      {change.path}
                    </code>
                    <div className="ps-4">
                      <span className="text-statusDanger line-through">
                        {render(change.before)}
                      </span>{" "}
                      <span className="text-statusSuccess">{render(change.after)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </Card>

        {/* -- who changed what ------------------------------------------------------ */}
        <Card className="flex flex-col gap-3 p-4">
          <h3 className="text-heading-sm">{t("versions.changeLog")}</h3>
          <ul className="space-y-1">
            {entries.map((entry) => (
              <li key={entry.id} className="text-body-sm">
                <span className="font-mono text-label-md text-textMuted">
                  {entry.at.slice(0, 16).replace("T", " ")}
                </span>{" "}
                <strong>{entry.actor}</strong>{" "}
                <code className="font-mono text-label-md">{entry.action}</code>{" "}
                <span className="text-textMuted">{entry.entity}</span>
              </li>
            ))}
            {entries.length === 0 ? (
              <li className="text-body-sm text-textMuted">{t("versions.noChanges")}</li>
            ) : null}
          </ul>
        </Card>
      </div>

      <Modal
        open={rolling !== null}
        onClose={() => setRolling(null)}
        title={t("versions.rollbackTitle")}
        closeLabel={t("actions.close", { ns: "translation" })}
      >
        <div className="flex flex-col gap-4">
          <p className="max-w-prose text-body-md">
            {t("versions.rollbackExplain", { label: rolling?.label ?? "" })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={async () => {
                if (!rolling) return;
                const ok = await save(
                  () => rollbackVersion(rolling.id),
                  t("versions.rolledBack", { label: rolling.label }),
                );
                setRolling(null);
                if (ok) await load();
              }}
            >
              {t("versions.rollbackConfirm")}
            </Button>
            <Button variant="secondary" onClick={() => setRolling(null)}>
              {t("actions.cancel", { ns: "translation" })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function render(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}
